import rawPriceBook from "../aiPriceBook.json";
import { canonicalUsageModel } from "./aiUsageModels";

import {
  AiModelRate,
  AiRateUnit,
  AiResolutionBucket,
  PRICE_BOOK_PATH,
  PriceBook,
  ProviderDefault,
  RESOLUTION_BUCKETS,
  RateOverrides,
  entryKeyOf,
  loadPriceBook,
  localDayOf,
  providerDefaultKeyOf,
  rateKeyOf,
  todayDay,
  windowContains,
} from "./aiPriceBook";

export type {
  AiModelRate,
  AiRateDim,
  AiRateUnit,
  AiResolutionBucket,
  AiTokenTier,
  PriceBook,
  ProviderDefault,
  RateOverrides,
} from "./aiPriceBook";

/**
 * ราคาต่อหน่วยของ AI model (USD) — ราคาทั้งหมดมาจาก price book (aiPriceBook.json) ไม่ hard-code ที่นี่
 *
 * - 1 แถวการใช้งาน = 1 variant = 1 ราคา (เช่น "1080P", "720P/noaudio", "image", "hour", "in"/"out")
 * - override (ราคาที่แก้ในแท็บ ราคา Model, บันทึกใน localStorage) ทับ price book ผ่าน `resolveRate`
 * - ทุกฟังก์ชัน lookup รับ `book` (default = PRICE_BOOK ที่ bundle มา) เพื่อให้ test ฉีด fixture ได้
 */

// รูปร่างขั้นต่ำของ usage ที่ module นี้ต้องรู้ (UsageLine ใน aiModelCost เข้ากันได้)
export type UsageShape = {
  service: string;
  provider: string;
  model: string;
  resolution?: string | null;
  audio_enabled?: boolean | null;
  images: number;
  video_seconds: number;
  audio_seconds: number;
  input_tokens: number;
  output_tokens: number;
  at?: string | null; // วันที่/เวลา (ISO หรือ YYYY-MM-DD) ที่ call เกิดขึ้น — ใช้เลือกราคาตามช่วงวันที่ (ไม่ระบุ = วันนี้)
};

// ─── Price book ──────────────────────────────────────────────────────────────

const loaded = loadPriceBook(rawPriceBook);
if (loaded.issues.length && process.env.NODE_ENV !== "test") {
  console.warn(`[MeGen] ${PRICE_BOOK_PATH} มีรายการที่ต้องแก้:\n- ${loaded.issues.join("\n- ")}`);
}

export const RAW_PRICE_BOOK: unknown = rawPriceBook; // ต้นฉบับ JSON (ใช้ตอน export คง _readme)
export const PRICE_BOOK: PriceBook = loaded.book;
export const PRICE_BOOK_ISSUES: string[] = loaded.issues;

type BookIndex = { byKey: Map<string, AiModelRate[]>; byModel: Map<string, AiModelRate[]> };
const indexCache = new WeakMap<PriceBook, BookIndex>();

const pushTo = (map: Map<string, AiModelRate[]>, key: string, rate: AiModelRate): void => {
  const arr = map.get(key);
  if (arr) arr.push(rate);
  else map.set(key, [rate]);
};

const indexOf = (book: PriceBook): BookIndex => {
  let idx = indexCache.get(book);
  if (!idx) {
    // 1 key อาจมีหลายรายการ (ราคาแต่ละช่วงวันที่ของ model เดียวกัน) → เก็บเป็น array แล้วเลือกตอน lookup
    const byKey = new Map<string, AiModelRate[]>();
    // model-only: รองรับ provider alias ใน log (gemini/google) — ใช้เฉพาะ provider ที่ไม่มี provider_defaults
    const byModel = new Map<string, AiModelRate[]>();
    book.models.forEach((r) => {
      pushTo(byKey, r.key, r);
      pushTo(byModel, r.model.toLowerCase(), r);
    });
    idx = { byKey, byModel };
    indexCache.set(book, idx);
  }
  return idx;
};

// รายการของ model นี้ที่มีผลในวัน `day`: รายการมีวันที่ที่หน้าต่างครอบวันชนะ (ตัวที่ start ช้าสุด)
// ตามด้วยรายการไม่มีวันที่ (ใช้ได้ทุกวัน) — ไม่มีรายการไหนตรงวัน = undefined (วันนอกทุกช่วงของ model)
const pickEntryForDay = (entries: AiModelRate[] | undefined, day: string): AiModelRate | undefined => {
  if (!entries || !entries.length) return undefined;
  let dated: AiModelRate | undefined;
  let undated: AiModelRate | undefined;
  entries.forEach((r) => {
    if (r.startDate === undefined && r.endDate === undefined) {
      if (!undated) undated = r;
    } else if (windowContains(r, day) && (!dated || (r.startDate || "") > (dated.startDate || ""))) {
      dated = r;
    }
  });
  return dated || undated;
};

export const providerDefaultOf = (
  provider: string | null | undefined,
  book: PriceBook = PRICE_BOOK
): ProviderDefault | undefined => book.providerDefaults[(provider || "").toLowerCase()];

// ─── Lookup ──────────────────────────────────────────────────────────────────

const SNAPSHOT_SUFFIX = /-\d{4}-\d{2}-\d{2}$/;
const stripSnapshot = (model: string): string => model.replace(SNAPSHOT_SUFFIX, "");

export type RateLookup = { rate: AiModelRate; fallbackFrom?: string }; // fallbackFrom = ที่มาของราคาที่ยืมมา (แสดงเป็น hint)

// ลำดับ: canonicalize historical alias → key ตรง → key ตัด snapshot → (provider มี provider_defaults
// → หยุด ไม่ alias ข้าม provider) → model-only → model-only ตัด snapshot → null
// `at` = วันที่ของแถว usage — model ที่มีหลายช่วงราคาจะได้รายการของวันนั้น ๆ (ไม่ระบุ = วันนี้)
export const findDefaultRate = (
  provider: string | null | undefined,
  model: string | null | undefined,
  book: PriceBook = PRICE_BOOK,
  at?: string | null
): RateLookup | null => {
  const { byKey, byModel } = indexOf(book);
  const day = localDayOf(at) || todayDay();
  const p = (provider || "").toLowerCase();
  const rawModel = (model || "").toLowerCase();
  const m = canonicalUsageModel(p, rawModel);
  const aliasFrom = m !== rawModel ? m : undefined;
  const base = stripSnapshot(m);

  const exact = pickEntryForDay(byKey.get(`${p}|${m}`), day);
  if (exact) return { rate: exact, fallbackFrom: aliasFrom };

  const snap = base !== m ? pickEntryForDay(byKey.get(`${p}|${base}`), day) : undefined;
  if (snap) return { rate: snap, fallbackFrom: aliasFrom || snap.model };

  // local|lipsync ต้องฟรี ห้ามไปยืม comfyui|lipsync (gpu_hour)
  if (providerDefaultOf(p, book)) return null;

  const alias = pickEntryForDay(byModel.get(m), day);
  if (alias) return { rate: alias, fallbackFrom: aliasFrom };

  const aliasSnap = base !== m ? pickEntryForDay(byModel.get(base), day) : undefined;
  if (aliasSnap) return { rate: aliasSnap, fallbackFrom: aliasFrom || aliasSnap.model };

  return null;
};

export const inferUnit = (u: UsageShape): AiRateUnit => {
  if (u.service === "ffmpeg") return "gpu_hour";
  if (u.service === "image" || u.images > 0) return "image";
  if (u.service === "video" || u.video_seconds > 0) return "second";
  if (u.input_tokens > 0 || u.output_tokens > 0) return "token_1m";
  if (u.audio_seconds > 0) return "second";
  return "call";
};

const zeroPrices = (unit: AiRateUnit): Record<string, number> => {
  if (unit === "image") return { image: 0 };
  if (unit === "token_1m") return { in: 0, out: 0 };
  if (unit === "gpu_hour") return { hour: 0 };
  if (unit === "call") return { call: 0 };
  return { all: 0 };
};

// rate จาก provider_defaults (model ของ provider นั้นที่ไม่มี entry ใน price book)
export const rateFromProviderDefault = (u: UsageShape, book: PriceBook = PRICE_BOOK): AiModelRate | null => {
  const def = providerDefaultOf(u.provider, book);
  if (!def) return null;
  const unit = def.unit || inferUnit(u);
  const dims = unit === "second" ? def.dims || [] : [];
  return {
    key: rateKeyOf(u.provider, canonicalUsageModel(u.provider, u.model)), // override ราย model ยังใช้ได้
    provider: u.provider,
    model: canonicalUsageModel(u.provider, u.model),
    service: def.service || u.service,
    unit,
    dims,
    defaultResolution: def.defaultResolution || (dims.includes("resolution") ? "1080P" : undefined),
    prices: def.free ? { ...zeroPrices(unit), ...def.prices } : { ...def.prices },
    free: def.free,
    inferred: true,
    note: def.note,
  };
};

// model ที่ไม่มีทั้งใน price book และ provider_defaults → เดา unit จาก usage ราคาว่าง (ยังไม่ได้ตั้งราคา)
export const inferRate = (u: UsageShape): AiModelRate => ({
  key: rateKeyOf(u.provider, canonicalUsageModel(u.provider, u.model)),
  provider: u.provider,
  model: canonicalUsageModel(u.provider, u.model),
  service: u.service,
  unit: inferUnit(u),
  dims: [],
  prices: {},
  free: false,
  inferred: true,
});

/**
 * ราคาช่องนี้ถูกผู้ใช้แก้ไปหรือยัง
 *
 * กฎ: model ที่ไม่มีใน price book (`inferred`) ให้ถือว่าถูกแก้ด้วย ถ้า **ค่าเริ่มต้นของ provider** (`provider|*`)
 * ถูกแก้ เพราะราคาที่ใช้จริงของแถวนั้นมาจาก override ตัวนั้น (ดู resolveRate)
 * รับ predicate เข้ามาเพื่อไม่ให้ module นี้ต้องรู้จัก store/hook
 */
export const isRateOverridden = (
  rate: AiModelRate,
  variant: string,
  isOverridden: (key: string, variant: string) => boolean
): boolean =>
  isOverridden(entryKeyOf(rate), variant) ||
  (!!rate.inferred && isOverridden(providerDefaultKeyOf(rate.provider), variant));

// rate ที่ใช้จริง = price book / provider default / inferred ทับด้วย override
//   provider default → override ของ `${provider}|*` ก่อน แล้ว override ราย model ทับอีกที
//   รายการมีวันที่ → override ผูกกับช่วงของมันเอง (entryKeyOf) ไม่รั่วไปช่วงอื่นของ model เดียวกัน
export const resolveRate = (u: UsageShape, overrides: RateOverrides, book: PriceBook = PRICE_BOOK): RateLookup => {
  const found = findDefaultRate(u.provider, u.model, book, u.at);
  let base: AiModelRate;
  let fallbackFrom = found?.fallbackFrom;
  let providerOv: Record<string, number> | undefined;

  if (found) {
    base = found.rate;
  } else {
    const fromDefault = rateFromProviderDefault(u, book);
    if (fromDefault) {
      base = fromDefault;
      fallbackFrom = `ค่าเริ่มต้นของ ${u.provider}`;
      providerOv = overrides[providerDefaultKeyOf(u.provider)];
    } else {
      base = inferRate(u);
    }
  }

  const ov = overrides[entryKeyOf(base)];
  const service = base.service || u.service;
  if (!ov && !providerOv && service === base.service) return { rate: base, fallbackFrom };

  return {
    rate: { ...base, service, prices: { ...base.prices, ...(providerOv || {}), ...(ov || {}) } },
    fallbackFrom,
  };
};

// ─── Resolution / variant ────────────────────────────────────────────────────

const bucketsByCloseness = (n: number): AiResolutionBucket[] =>
  [...RESOLUTION_BUCKETS].sort((a, b) => Math.abs(n - parseInt(a, 10)) - Math.abs(n - parseInt(b, 10)));

const PIXEL_SIZE_RE = /(\d{3,4})\s*[X*×]\s*(\d{3,4})/;

// "720P" / "1080p" / "1280*720" / "1920x1080" / "720×1280" → bucket ที่ใกล้สุด (ใช้ด้านสั้น → แนวตั้ง 9:16 ถูกด้วย)
export const resolutionBucketOf = (resolution?: string | null): AiResolutionBucket | null => {
  if (!resolution) return null;
  const s = String(resolution).trim().toUpperCase();

  const p = s.match(/^(\d{3,4})\s*P$/);
  if (p) return bucketsByCloseness(Number(p[1]))[0];

  const wh = s.match(PIXEL_SIZE_RE);
  if (wh) return bucketsByCloseness(Math.min(Number(wh[1]), Number(wh[2])))[0];

  return null;
};

export type VariantResult = { variant: string; reasons: string[] }; // reasons ไม่ว่าง = ประมาณการ

export const variantOf = (
  rate: AiModelRate,
  u: { resolution?: string | null; audio_enabled?: boolean | null; model?: string | null; provider?: string }
): VariantResult => {
  if (rate.unit === "image") return { variant: "image", reasons: [] };
  if (rate.unit === "token_1m") return { variant: "tokens", reasons: [] };
  if (rate.unit === "gpu_hour") return { variant: "hour", reasons: [] };
  if (rate.unit === "call") return { variant: "call", reasons: [] };

  const dims = rate.dims || [];
  if (!dims.length) return { variant: "all", reasons: [] };

  const reasons: string[] = [];
  let variant = "all";

  if (dims.includes("resolution")) {
    const bucket = resolutionBucketOf(u.resolution);
    if (bucket) {
      variant = bucket;
      // แถว wan2.7 ก่อน 2026-09: backend ส่ง `size` แบบ pixel ("1280*720") ซึ่ง wan2.7 ไม่มี parameter
      // นี้ ค่าจึงถูก ignore และทุก call ถูกบิลจริงที่ default 1080P — ต้องใช้ราคา 1080P ไม่เช่นนั้น
      // ต้นทุนย้อนหลังจะต่ำกว่าที่จ่ายจริง (~1.5 เท่า); wan2.6 ยังรับ pixel size จริง จึงคง bucket ตาม pixel
      if (
        u.resolution &&
        PIXEL_SIZE_RE.test(String(u.resolution)) &&
        canonicalUsageModel(u.provider || rate.provider, String(u.model || "").toLowerCase()).startsWith("wan2.7")
      ) {
        variant = "1080P";
        reasons.push("wan2.7 แถวเก่า (resolution แบบ pixel) ถูกบิลจริงที่ 1080P");
      }
    } else {
      variant = rate.defaultResolution || "1080P";
      reasons.push(`ไม่ทราบ resolution → ใช้ราคา ${variant}`);
    }
  }

  if (dims.includes("audio")) {
    if (u.audio_enabled === false) variant = `${variant}/noaudio`;
    else if (u.audio_enabled === null || u.audio_enabled === undefined) reasons.push("ไม่ทราบสถานะเสียง → คิดราคามีเสียง");
  }

  return { variant, reasons };
};

// "1080P" → "1080P", "720P/noaudio" → "720P · ไม่มีเสียง", "all/noaudio" → "ไม่มีเสียง", "all"/"image"/"tokens"/"hour"/"call" → ""
export const variantLabelOf = (variant: string): string => {
  const [res, flag] = variant.split("/");
  const parts: string[] = [];
  if (/^\d{3,4}P$/.test(res)) parts.push(res);
  if (flag === "noaudio") parts.push("ไม่มีเสียง");
  return parts.join(" · ");
};

export type PriceLookup = { price: number | undefined; reason?: string };

const labelOrAll = (variant: string): string => variantLabelOf(variant) || "ราคาเดียว (all)";

// หาราคา unit=second ของ res (NNNP หรือ all) + suffix ("/noaudio" หรือ "") พร้อม fallback
const secondPriceFor = (rate: AiModelRate, res: string, suffix: string): PriceLookup | null => {
  const requested = `${res}${suffix}`;
  const direct = rate.prices[requested];
  if (direct !== undefined) return { price: direct };

  const candidates: string[] = [];
  if (/^\d{3,4}P$/.test(res)) {
    bucketsByCloseness(parseInt(res, 10)).forEach((b) => candidates.push(`${b}${suffix}`));
    candidates.push(`all${suffix}`);
  } else {
    const start = rate.defaultResolution || "1080P";
    [start, ...bucketsByCloseness(parseInt(start, 10))].forEach((b) => {
      if (!candidates.includes(`${b}${suffix}`)) candidates.push(`${b}${suffix}`);
    });
  }

  for (const candidate of candidates) {
    if (candidate !== requested && rate.prices[candidate] !== undefined) {
      return { price: rate.prices[candidate], reason: `ไม่มีราคา ${labelOrAll(requested)} → ใช้ราคา ${labelOrAll(candidate)}` };
    }
  }
  return null;
};

// ราคาของ variant — bucket/all ที่ไม่มีราคา → ใช้ตัวที่ใกล้สุดที่มี (F1 safety net), ไม่มีราคาปิดเสียง → คิดราคามีเสียง; ฟรี → 0
export const priceFor = (rate: AiModelRate, variant: string): PriceLookup => {
  const direct = rate.prices[variant];
  if (direct !== undefined) return { price: direct };

  if (rate.unit === "second") {
    const [res, flag] = variant.split("/");
    const suffix = flag ? `/${flag}` : "";
    const withSuffix = secondPriceFor(rate, res, suffix);
    if (withSuffix) return withSuffix;
    if (suffix) {
      const withAudio = secondPriceFor(rate, res, "");
      if (withAudio) {
        return { price: withAudio.price, reason: `ไม่มีราคาปิดเสียง → คิดราคามีเสียง${withAudio.reason ? ` (${withAudio.reason})` : ""}` };
      }
    }
  }

  if (rate.free) return { price: 0 };
  return { price: undefined };
};

export const unitSuffixOf = (unit: AiRateUnit): string => {
  if (unit === "second") return "/วิ";
  if (unit === "image") return "/รูป";
  if (unit === "gpu_hour") return "/ชม. GPU";
  if (unit === "call") return "/call";
  return "/1M tokens";
};
